import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Menu as IconeMenu } from 'lucide-react';
import { useSessao } from '../auth/SessaoContext';
import { iniciais } from '../util/formato';
import { Marca } from './Marca';
import { AvisoVerificacao } from './AvisoVerificacao';
import { Botao, DivisorMenu, ItemMenu, Menu } from './ui';
import { cx } from './ui/cx';
import css from './Estrutura.module.css';

export interface ItemNavegacao {
  para: string;
  rotulo: string;
  icone: ReactNode;
  /** Só a rota exata marca como ativa. */
  exato?: boolean;
  /** Número no badge (mensagens novas). */
  contador?: number;
}

export interface GrupoNavegacao {
  titulo?: string;
  itens: ItemNavegacao[];
}

export interface AtalhoUsuario {
  rotulo: string;
  icone: ReactNode;
  para: string;
}

/**
 * Esqueleto das áreas logadas (painel do cliente e administração): lateral com
 * navegação agrupada, cabeçalho com o menu do usuário e o conteúdo da rota.
 *
 * No celular a lateral vira gaveta: abre no botão do topo e fecha sozinha ao
 * trocar de página — sem isso o menu ficaria aberto em cima da tela escolhida.
 */
export function Estrutura({
  grupos,
  contexto,
  chamada,
  rodapeLateral,
  topo,
  atalhosUsuario = [],
  inicio,
}: {
  grupos: GrupoNavegacao[];
  /** Selo ao lado da marca ("Admin"). */
  contexto?: string;
  /** Botão de destaque no topo da lateral. */
  chamada?: ReactNode;
  rodapeLateral?: ReactNode;
  /** Itens extras no cabeçalho, à esquerda do usuário. */
  topo?: ReactNode;
  atalhosUsuario?: AtalhoUsuario[];
  /** Rota do logotipo. */
  inicio: string;
}) {
  const { usuario, sair } = useSessao();
  const navegar = useNavigate();
  const local = useLocation();
  const [gaveta, setGaveta] = useState(false);

  useEffect(() => {
    setGaveta(false);
  }, [local.pathname]);

  async function encerrar() {
    await sair();
    navegar('/', { replace: true });
  }

  return (
    <div className={css.app}>
      <div
        className={cx(css.veu, gaveta && css.veuAberto)}
        onClick={() => setGaveta(false)}
        aria-hidden="true"
      />

      <aside className={cx(css.lateral, gaveta && css.lateralAberta)}>
        <div className={css.marca}>
          <Link to={inicio} aria-label="Início">
            <Marca cor="#fff" tamanho={22} fonte={18} />
          </Link>
          {contexto && <span className={css.contexto}>{contexto}</span>}
        </div>

        {chamada && <div className={css.chamada}>{chamada}</div>}

        <nav className={css.navegacao} aria-label="Navegação principal">
          {grupos.map((g, i) => (
            <div key={g.titulo ?? i} className={css.grupo}>
              {g.titulo && <div className={css.grupoTitulo}>{g.titulo}</div>}
              <ul className={css.itens}>
                {g.itens.map((item) => (
                  <li key={item.para}>
                    <NavLink
                      to={item.para}
                      end={item.exato}
                      className={({ isActive }) => cx(css.item, isActive && css.ativo)}
                    >
                      {item.icone}
                      <span>{item.rotulo}</span>
                      {!!item.contador && (
                        <span className={css.contador} aria-label={`${item.contador} nova(s)`}>
                          {item.contador > 99 ? '99+' : item.contador}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {rodapeLateral && <div className={css.rodapeLateral}>{rodapeLateral}</div>}
      </aside>

      <div className={css.coluna}>
        <header className={css.topo}>
          <div className={css.topoEsquerda}>
            <Botao
              aparencia="discreto"
              soIcone
              className={css.hamburguer}
              aria-label="Abrir menu"
              aria-expanded={gaveta}
              onClick={() => setGaveta(true)}
              icone={<IconeMenu />}
            />
            <Link to={inicio} className={css.marcaTopo} aria-label="Início">
              <Marca tamanho={20} fonte={16} />
            </Link>
          </div>

          <div className={css.topoDireita}>
            {topo}
            <Menu
              cabecalho={
                <>
                  <strong>{usuario?.nome}</strong>
                  <div className={css.menuEmail}>{usuario?.email}</div>
                </>
              }
              gatilho={({ alternar, ...aria }) => (
                <button type="button" className={css.usuario} onClick={alternar} {...aria}>
                  <span className={css.avatar} aria-hidden="true">
                    {iniciais(usuario?.nome ?? '')}
                  </span>
                  <span className={css.usuarioNome}>
                    <strong>{usuario?.nome}</strong>
                    <span>{usuario?.email}</span>
                  </span>
                  <ChevronDown aria-hidden="true" />
                  <span className="sr-only">Menu da conta</span>
                </button>
              )}
            >
              {atalhosUsuario.map((a) => (
                <ItemMenu key={a.para} icone={a.icone} onClick={() => navegar(a.para)}>
                  {a.rotulo}
                </ItemMenu>
              ))}
              {atalhosUsuario.length > 0 && <DivisorMenu />}
              <ItemMenu icone={<LogOut />} onClick={() => void encerrar()}>
                Sair
              </ItemMenu>
            </Menu>
          </div>
        </header>

        <AvisoVerificacao />

        <main className={css.conteudo}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
